/**
 * Hook for fetching license plans from database
 * This ensures pricing is always synchronized with the database
 */

import { fetchLicensePlans, SUBSCRIPTION_PLANS, SubscriptionPlan } from '@/constants/pricing';
import { useQuery, useQueryClient } from '@tanstack/react-query';

const QUERY_KEY = ['license-plans'];
const STALE_TIME = 1000 * 60 * 5; // 5 minutes
const GC_TIME = 1000 * 60 * 10; // 10 minutes

/**
 * Hook to fetch license plans from database with fallback to static plans
 */
export function useLicensePlans() {
  return useQuery({
    queryKey: QUERY_KEY,
    queryFn: fetchLicensePlans,
    staleTime: STALE_TIME,
    gcTime: GC_TIME,
    // Always provide fallback data
    placeholderData: SUBSCRIPTION_PLANS,
    // Don't retry too aggressively since we have fallback data
    retry: 1,
    // Don't refetch on window focus to avoid unnecessary requests
    refetchOnWindowFocus: false,
  });
}

/**
 * Get available upgrade plans for a user based on their current plan
 * Uses database-backed plans when available
 */
export function useAvailableUpgrades(currentPlan: string) {
  const { data: plans = SUBSCRIPTION_PLANS } = useLicensePlans();

  // Filter out Ultra and Trial plans from all operations
  const filteredPlans = plans.filter(plan =>
    plan.id !== 'trial' && plan.id !== 'ultra' && plan.name.toLowerCase() !== 'ultra'
  );

  const currentPlanIndex = filteredPlans.findIndex(plan =>
    plan.name.toLowerCase() === currentPlan.toLowerCase()
  );

  // If user is on trial or plan not found, show all paid plans (excluding trial and ultra)
  if (currentPlanIndex === -1 || currentPlan.toLowerCase() === 'trial') {
    return filteredPlans;
  }

  // Return plans that are higher than current plan
  return filteredPlans.slice(currentPlanIndex + 1);
}

/**
 * Hook to invalidate license plans cache
 * Use this to force refresh the plans from database
 */
export function useInvalidateLicensePlans() {
  const queryClient = useQueryClient();

  return () => {
    queryClient.invalidateQueries({
      queryKey: QUERY_KEY,
    });
    console.log('License plans cache invalidated');
  };
}

/**
 * Get plan by ID using database-backed plans
 */
export function usePlanById(planId: string): SubscriptionPlan | undefined {
  const { data: plans = SUBSCRIPTION_PLANS } = useLicensePlans();
  return plans.find(plan => plan.id === planId);
}

/**
 * Get plan by name using database-backed plans
 */
export function usePlanByName(planName: string): SubscriptionPlan | undefined {
  const { data: plans = SUBSCRIPTION_PLANS } = useLicensePlans();
  return plans.find(plan => 
    plan.name.toLowerCase() === planName.toLowerCase()
  );
}
